import React from "react";
import { Autocomplete, Box, Checkbox, FormControlLabel, MenuItem, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { PersonAvatar, T, TYPE } from "../user/complaints/shared";
import { usePm } from "./PmContext";
import { TypeIcon } from "./ui";

// Form inputs shared by PM dialogs and settings.

export const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: T.radiusControl, bgcolor: "#fff", fontSize: TYPE.body } };

export const Select = ({ label, value, onChange, options, allowEmpty, emptyLabel = "None", size = "small", sx, ...rest }) => (
  <TextField select fullWidth size={size} label={label} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} sx={{ ...fieldSx, ...sx }} {...rest}>
    {allowEmpty && <MenuItem value=""><em>{emptyLabel}</em></MenuItem>}
    {options.map((o) => (
      <MenuItem key={String(o.value)} value={o.value} disabled={o.disabled}>{o.label}</MenuItem>
    ))}
  </TextField>
);

export const PersonPicker = ({ label = "Assignee", value, onChange, allowEmpty = true, people: only, size = "small", multiple = false, disabled = false, helperText, sx }) => {
  const { people } = usePm();
  const list = only || people;
  const byId = new Map(list.map((p) => [String(p.id), p]));
  const current = multiple ? (value || []).map((v) => byId.get(String(v))).filter(Boolean) : byId.get(String(value)) || null;
  return (
    <Autocomplete
      size={size}
      multiple={multiple}
      options={list}
      value={current}
      onChange={(_, v) => onChange(multiple ? v.map((x) => String(x.id)) : v ? String(v.id) : null)}
      getOptionLabel={(o) => o?.name || ""}
      isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
      disableClearable={!allowEmpty && !multiple}
      disabled={disabled}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={String(o.id)}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonAvatar name={o.name} size={22} />
            <Box>
              <Typography sx={{ fontSize: TYPE.small }}>{o.name}</Typography>
              <Typography sx={{ fontSize: "0.7rem", color: T.inkMuted }}>{o.email}</Typography>
            </Box>
          </Stack>
        </Box>
      )}
      renderInput={(params) => <TextField {...params} label={label} placeholder={allowEmpty && !multiple ? "Unassigned" : ""} helperText={helperText} sx={fieldSx} />}
      sx={sx}
    />
  );
};

export const TypeSelect = ({ value, onChange, typeIds, level, label = "Issue type", sx }) => {
  const { issueTypes } = usePm();
  const list = issueTypes.filter((t) => t.isActive && (!typeIds || typeIds.map(String).includes(String(t._id))) && (level === undefined || [].concat(level).includes(t.level)));
  return (
    <TextField select fullWidth size="small" label={label} value={value || ""} onChange={(e) => onChange(e.target.value)} sx={{ ...fieldSx, ...sx }}>
      {list.map((t) => (
        <MenuItem key={t._id} value={t._id}>
          <Stack direction="row" spacing={1} alignItems="center"><TypeIcon typeId={t._id} size={14} /><span>{t.name}</span></Stack>
        </MenuItem>
      ))}
    </TextField>
  );
};

export const PrioritySelect = ({ value, onChange, label = "Priority", sx }) => {
  const { priorities } = usePm();
  return <Select label={label} value={value} onChange={onChange} options={priorities.filter((p) => p.isActive).map((p) => ({ value: p._id, label: p.name }))} sx={sx} />;
};

export const LabelsInput = ({ value = [], onChange, options = [], label = "Labels" }) => (
  <Autocomplete multiple freeSolo size="small" options={options} value={value}
    onChange={(_, v) => onChange([...new Set(v.map((x) => String(x).trim().toLowerCase().replace(/\s+/g, "-")).filter(Boolean))])}
    renderInput={(params) => <TextField {...params} label={label} placeholder="Type and press Enter" sx={fieldSx} />} />
);

export const MultiSelect = ({ label, value = [], onChange, options }) => (
  <Autocomplete multiple size="small" options={options} value={options.filter((o) => value.map(String).includes(String(o.value)))}
    getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => String(a.value) === String(b.value)}
    onChange={(_, v) => onChange(v.map((o) => o.value))}
    renderInput={(params) => <TextField {...params} label={label} sx={fieldSx} />} />
);

export const dateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : "");

/** Custom field input by type (19 types; reference types use workspace/project data). */
export const CustomFieldInput = ({ field, value, onChange, refs = {} }) => {
  const common = { fullWidth: true, size: "small", label: `${field.label}${field.required ? " *" : ""}`, sx: fieldSx };
  switch (field.type) {
    case "textarea":
      return <TextField {...common} multiline minRows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "number": case "decimal":
      return <TextField {...common} type="number" inputProps={{ step: field.type === "decimal" ? "any" : 1 }} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
    case "email": case "phone": case "url": case "text":
      return <TextField {...common} type={field.type === "email" ? "email" : field.type === "url" ? "url" : "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
    case "date":
      return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} value={dateInput(value)} onChange={(e) => onChange(e.target.value || null)} />;
    case "datetime":
      return <TextField {...common} type="datetime-local" InputLabelProps={{ shrink: true }} value={value ? new Date(value).toISOString().slice(0, 16) : ""} onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)} />;
    case "dropdown":
      return <Select {...common} value={value} onChange={onChange} allowEmpty options={field.options.map((o) => ({ value: o, label: o }))} />;
    case "multiselect":
      return <MultiSelect label={common.label} value={value || []} onChange={onChange} options={field.options.map((o) => ({ value: o, label: o }))} />;
    case "radio":
      return (
        <Box>
          <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{common.label}</Typography>
          <RadioGroup row value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
            {field.options.map((o) => <FormControlLabel key={o} value={o} control={<Radio size="small" />} label={<Typography sx={{ fontSize: TYPE.small }}>{o}</Typography>} />)}
          </RadioGroup>
        </Box>
      );
    case "checkbox":
      return <FormControlLabel control={<Checkbox size="small" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>{field.label}</Typography>} />;
    case "user":
      return <PersonPicker label={common.label} value={value} onChange={onChange} />;
    case "team":
      return <Select {...common} value={value} onChange={onChange} allowEmpty options={(refs.teams || []).map((t) => ({ value: t._id, label: t.name }))} />;
    case "project":
      return <Select {...common} value={value} onChange={onChange} allowEmpty options={(refs.projects || []).map((p) => ({ value: p._id, label: `${p.key} · ${p.name}` }))} />;
    case "sprint":
      return <Select {...common} value={value} onChange={onChange} allowEmpty options={(refs.sprints || []).map((s) => ({ value: s._id, label: s.name }))} />;
    case "version":
      return <Select {...common} value={value} onChange={onChange} allowEmpty options={(refs.versions || []).map((v) => ({ value: v._id, label: v.name }))} />;
    case "issue":
      return <TextField {...common} placeholder="Issue key, e.g. ABC-12" value={value ?? ""} onChange={(e) => onChange(e.target.value.toUpperCase())} />;
    default:
      return <TextField {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
};

/** Custom fields that apply to a project + issue type. */
export const applicableFields = (fields, projectId, typeId) =>
  (fields || []).filter((f) => f.isActive && (!f.projectIds?.length || f.projectIds.map(String).includes(String(projectId))) && (!f.issueTypeIds?.length || f.issueTypeIds.map(String).includes(String(typeId))));
